import { useState } from "react"
import { useTranslation } from "react-i18next"

import { toAppLocale } from "@/lib/i18n/locales"
import type { Locale } from "@/types"

/**
 * OKS-28 / OKS-1025 — a segment's « Créé » date as a coarse relative string
 * (« il y a 3 jours », « hier », "3 days ago"). The absolute ISO lives on the
 * parent cell's `title`.
 *
 * Wording comes from `Intl.RelativeTimeFormat` with `numeric: "auto"`, in
 * hour / day / month / year steps. That is deliberately NOT
 * `formatStrictDistance` (`lib/relative-time.ts`): moving onto date-fns would
 * rewrite « hier » as « il y a 1 jour ».
 *
 * ## Where the language comes from (OKS-1025)
 *
 * The i18next instance from React context (`useTranslation().i18n`). Before
 * OKS-1025 this read `document.documentElement.lang` and fell back to "fr"
 * without a `document` — so an English brand's SSR HTML carried French dates
 * until the client re-rendered.
 *
 * ⚠️ Context instance, never the `i18next` singleton: the SSR sidecar renders
 * each request with its own instance, and a singleton read would give every
 * request whichever language the process last saw.
 *
 * `now` is captured once at mount (`useState` init) so the text is stable
 * across re-renders; the language is read on EVERY render, so a language
 * switch re-renders it in place.
 */
export function SegmentRelativeDate({ iso }: { iso: string }) {
  const { i18n } = useTranslation()
  const [nowMs] = useState(() => Date.now())

  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return <span>{iso}</span>

  const locale: Locale = toAppLocale(i18n.language)
  const diffSeconds = (date.getTime() - nowMs) / 1000
  const diffDays = diffSeconds / 86_400

  let value: number
  let unit: Intl.RelativeTimeFormatUnit

  if (Math.abs(diffDays) < 1) {
    const diffHours = diffSeconds / 3_600
    value = Math.round(diffHours)
    unit = "hour"
  } else if (Math.abs(diffDays) < 30) {
    value = Math.round(diffDays)
    unit = "day"
  } else if (Math.abs(diffDays) < 365) {
    value = Math.round(diffDays / 30)
    unit = "month"
  } else {
    value = Math.round(diffDays / 365)
    unit = "year"
  }

  const formatted = formatRelative(locale, value, unit)
  return <span>{formatted ?? iso}</span>
}

// Not formatRelativeIntl: hour/day/month/year units with now frozen at mount — the helper has no month/year and reads the clock per call.
function formatRelative(
  locale: Locale,
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
): string | null {
  try {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      value,
      unit,
    )
  } catch {
    return null
  }
}
